import { getTgContext } from './tg';

export type SessionUser = {
  id: number;
  telegram_id: number;
  username: string | null;
  first_name: string;
  bid_balance: number;
};

export type Session = {
  token: string;
  expires_at: number;
  user: SessionUser;
};

const STORAGE_KEY = 'skinstep.session';

let memorySession: Session | null = null;
let pending: Promise<Session> | null = null;
const listeners = new Set<(s: Session | null) => void>();

function loadFromStorage(): Session | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    if (parsed.expires_at * 1000 <= Date.now() + 5_000) return null;
    return parsed;
  } catch {
    return null;
  }
}

function persist(session: Session | null): void {
  memorySession = session;
  try {
    if (session) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // private mode or quota — keep in-memory only
  }
  for (const cb of listeners) cb(session);
}

export function getSession(): Session | null {
  return memorySession;
}

export function getSessionToken(): string | null {
  return memorySession?.token ?? null;
}

export function onSessionChange(cb: (s: Session | null) => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

async function exchange(initData: string): Promise<Session> {
  const baseUrl = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
  const res = await fetch(`${baseUrl}/api/auth/session`, {
    method: 'POST',
    headers: {
      'X-Telegram-Init-Data': initData,
      Accept: 'application/json',
    },
  });
  if (!res.ok) {
    const payload = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(payload?.error?.message ?? `session exchange failed with ${res.status}`);
  }
  return (await res.json()) as Session;
}

export async function bootstrapSession(): Promise<Session> {
  if (memorySession) return memorySession;
  const cached = loadFromStorage();
  if (cached) {
    memorySession = cached;
    return cached;
  }
  return refreshSession();
}

export function refreshSession(): Promise<Session> {
  if (pending) return pending;
  const ctx = getTgContext();
  if (!ctx) {
    return Promise.reject(new Error('not running inside Telegram WebApp'));
  }
  pending = exchange(ctx.initData)
    .then((session) => {
      persist(session);
      return session;
    })
    .finally(() => {
      pending = null;
    });
  return pending;
}

export function clearSession(): void {
  persist(null);
}
